import React, { useEffect, useState, useCallback } from "react";
import styles from "./Accordion.module.css";
import { IPatient, IPatientRes } from "../../hooks/patients/index.type";
import BillingCard from "./BillingCard";
import TimerCard from "./Timer";
import ButtonCard from "./ButtonCard";
import NoteCard from "./NoteCard";
import ChatCard from "./ChatCard";
import { ButtonProps } from "../Modal/Button";
import AuditLogsModal from "../../pageComponents/Patients/AuditLogModal";
import { useGetBillingCycle } from "../../hooks/patients";
import { useFetchChat } from "../../hooks/chats";
import CarePlanModal from "../../pageComponents/Patients/CarePlanModal";
import IntakeFormViewModal from "../../pageComponents/Patients/IntakeFormViewModal";
import PreviewModalVersion2 from "../../pageComponents/Patients/PreviewModalVersion2";

interface AccordionRowProps {
  canEdit?: "yes" | "no";
  patient: IPatient;
  onClose: () => void;
  decryptPrivateKeyData: CryptoKey | null;
  isOpen: boolean;
  onSave: (data: IPatient) => void;
  isKeyDecrypted?: boolean;
  patientData?: IPatientRes;
  setPatientData?: React.Dispatch<
    React.SetStateAction<IPatientRes | undefined>
  >;
  intakeFormStatusLoading?: boolean;
  onIntakeFormUpdate?: () => void;
  onTimerStart?: () => void;
  onTimerStop?: () => void;
  getFloatingTimer?: () => void;
  getPatientData?: () => Promise<void>;
}

const AccordionRow: React.FC<AccordionRowProps> = ({
  canEdit,
  patient,
  decryptPrivateKeyData,
  patientData,
  setPatientData,
  intakeFormStatusLoading,
  onIntakeFormUpdate,
  onTimerStart,
  onTimerStop,
  getFloatingTimer,
  getPatientData,
}) => {
  const [currentModal, setCurrentModal] = React.useState<
    | ""
    | "billing-calendar"
    | "audit-logs"
    | "care-plan"
    | "intake-form"
    | "patient-dashboard"
  >("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedDate, setSelectedDate] = useState<{
    date: string;
    billingCycleId: string;
  } | null>(null);

  const [showAddNoteModal, setShowAddNoteModal] = React.useState(false);
  const [triggeredBy, setTriggeredBy] = useState<string>("New Note");
  const [billingSessionId, setBillingSessionId] = useState<string>("");

  const { getChatData, chatData, chatDataLoading, isAlertMessagesPresent } =
    useFetchChat({
      patientId: patient._id,
    });

  const { billingCycleData, getBillingCycleData, billingCycleLoading } =
    useGetBillingCycle({ patientId: patient._id });

  const refreshBillingData = useCallback(async () => {
    try {
      await getBillingCycleData();
    } catch (error) {
      console.error("Failed to refresh billing data:", error);
    }
  }, [getBillingCycleData]);

  useEffect(() => {
    getChatData();
  }, []);

  useEffect(() => {
    refreshBillingData();
  }, [refreshTrigger, refreshBillingData]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
    getBillingCycleData();
  };

  const buttonData: ButtonProps[] = [
    // {
    //   label: "Billing Calendar",
    //   icon: "icon-calendar2",
    //   onClick: () => setCurrentModal("billing-calendar"),
    //   disabled: patient.rtmStatus === "Inactive",
    // },
    {
      label: "Physician Report",
      icon: "icon-care-plan",
      onClick: () => setCurrentModal("patient-dashboard"),
      disabled: patient.rtmStatus === "Pending",
    },
    {
      label: "Audit Logs",
      icon: "icon-audit-log",
      onClick: () => setCurrentModal("audit-logs"),
      disabled: patient.rtmStatus === "Pending",
    },
    {
      label: "Intake Form",
      icon:
        canEdit === "yes" ? (
          <>
            {patient?.intakeForm?.some((record: any) => record.scale) ? (
              <svg className={styles.buttonIcon} viewBox="0 0 24 24">
                <use href="/assets/sprite.svg#icon-document" />
              </svg>
            ) : (
              <>
                <svg className={styles.intakeFormRedIcon} viewBox="0 0 24 24">
                  <use href="/assets/sprite.svg#icon-exclamation" />
                </svg>
                <svg className={styles.buttonIcon} viewBox="0 0 24 24">
                  <use href="/assets/sprite.svg#icon-document" />
                </svg>
              </>
            )}
          </>
        ) : (
          "icon-document"
        ),
      onClick: () => setCurrentModal("intake-form"),
      disabled: patient.rtmStatus === "Pending",
    },
    {
      label: "Transmission",
      icon: "icon-care-plan",
      onClick: () => setCurrentModal("care-plan"),
      disabled: patient.rtmStatus === "Pending",
    },
  ];

  const showModal = () => {
    switch (currentModal) {
      // case "billing-calendar":
      //   return (
      //     <BillingCalendar
      //       show
      //       onClose={() => setCurrentModal("")}
      //       patient={patient}
      //       onSuccess={async () => {
      //         try {
      //           const data = await getBillingCycleData();
      //           if (data?.data) {
      //             const updatedPatient = {
      //               ...patient,
      //               transmission: data.data.consolidatedBillingTransmission,
      //               requiredBillingTransmission:
      //                 data.data.totalBillingTransmission,
      //               timeSpent: data.data.consolidatedBillingMinutes,
      //               leftDays: data.data.totalCompletedDays,
      //               requiredBillingDays: data.data.totalBillingCycleDays,
      //               totalBillingCycleDays: data.data.totalBillingCycleDays,
      //               consolidatedBillingTransmission:
      //                 data.data.consolidatedBillingTransmission,
      //               totalBillingTransmission:
      //                 data.data.totalBillingTransmission,
      //               consolidatedBillingMinutes:
      //                 data.data.consolidatedBillingMinutes,
      //               totalBillingMinutes: data.data.totalBillingMinutes,
      //             };

      //             onSave(updatedPatient);

      //             setRefreshTrigger((prev) => prev + 1);
      //           }
      //         } catch (error) {
      //           console.error(
      //             "Error updating patient data after billing cycle change:",
      //             error
      //           );

      //           setRefreshTrigger((prev) => prev + 1);
      //         }
      //       }}
      //       durationDays={30}
      //     />
      //   );

      case "audit-logs":
        return (
          <AuditLogsModal
            show
            onClose={() => setCurrentModal("")}
            patient={patient}
          />
        );

      case "patient-dashboard":
        return (
          <PreviewModalVersion2
            show={true}
            onClose={() => {
              setCurrentModal("");
              setSelectedDate(selectedDate);
            }}
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            patient={patient}
          />
          // <PatientDashboard
          //   show={true}
          //   onClose={() => setCurrentModal("")}
          //   patient={patient}
          // />
        );

      case "intake-form":
        return (
          <IntakeFormViewModal
            show={true}
            onClose={() => setCurrentModal("")}
            patient={patient}
            canEdit={canEdit}
            intakeFormStatusLoading={intakeFormStatusLoading}
            onUpdateSuccess={onIntakeFormUpdate}
          />
        );

      case "care-plan":
        const transmissionTemplate =
          billingCycleData?.data?.plan?.transmissionTemplateId;
        if (!transmissionTemplate) return null;

        return (
          <CarePlanModal
            show
            onClose={() => setCurrentModal("")}
            patient={patient}
            transmissionTemplate={transmissionTemplate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={`${styles.leftSection}`}>
        <div
          className={
            patient.rtmStatus === "Inactive" ? styles.inactiveCardVisual : ""
          }
        >
          <BillingCard
            title="Billing Cycle"
            data={
              billingCycleData
                ? [
                    {
                      label: "Start Date",
                      value: billingCycleData?.data.billingCycleStartDate,
                    },
                    {
                      label: "Transmissions",
                      value:
                        billingCycleData?.data.consolidatedBillingTransmission +
                        "/" +
                        billingCycleData?.data.totalBillingTransmission,
                    },
                    {
                      label: "Days Left",
                      value:
                        billingCycleData?.data.totalCompletedDays +
                        "/" +
                        billingCycleData?.data.totalBillingCycleDays,
                    },
                    {
                      label: "Time Spent",
                      value:
                        billingCycleData?.data.consolidatedBillingMinutes +
                        "/" +
                        billingCycleData?.data.totalBillingMinutes,
                    },
                    {
                      label: "Billable On",
                      value: billingCycleData?.data.billingCycleEndDate,
                    },
                  ]
                : [
                    { label: "Start Date", value: "-" },
                    { label: "Transmissions", value: "-" },
                    { label: "Days Left", value: "-" },
                    { label: "Time Spent", value: "-" },
                    { label: "Billable On", value: "-" },
                  ]
            }
          />
        </div>

        <TimerCard
          patientId={patient._id ?? ""}
          clinicId={patient.associatedClinic?._id ?? ""}
          billingCycleId={billingCycleData?.data?._id}
          onTimerUpdate={handleRefresh}
          patientData={patientData}
          setPatientData={setPatientData}
          setBillingSessionId={setBillingSessionId}
          rtmStatus={patient.rtmStatus as "Active" | "Inactive" | "Pending"}
          isDisabled={
            patient.rtmStatus === "Inactive" || patient.rtmStatus === "Pending"
          }
          setShowAddNoteModal={setShowAddNoteModal}
          setTriggeredBy={setTriggeredBy}
          onTimerStart={onTimerStart}
          onTimerStop={onTimerStop}
          getFloatingTimer={getFloatingTimer}
          patientName={patient.name}
          getPatientData={getPatientData}
        />
        <ButtonCard buttonData={buttonData} />
      </div>

      <div
        className={`${styles.middleSection} ${
          patient.rtmStatus === "Inactive" ? styles.inactiveCardVisual : ""
        }`}
      >
        <NoteCard
          patientData={patient}
          // rtmStatus={
          //   (patient.rtmStatus as "Active" | "Inactive" | "Initialized") ||
          //   "Inactive"
          // }
          showAddNoteModal={showAddNoteModal}
          setShowAddNoteModal={setShowAddNoteModal}
          setTriggeredBy={setTriggeredBy}
          triggeredBy={triggeredBy}
          billingSessionId={billingSessionId}
        />
      </div>

      <div
        className={`${styles.rightSection} ${
          patient.rtmStatus === "Inactive" ? styles.inactiveCardVisual : ""
        }`}
      >
        <ChatCard
          patient={patient}
          chat={chatData}
          loading={chatDataLoading}
          isAlertMessagesPresent={isAlertMessagesPresent}
          decryptPrivateKeyData={decryptPrivateKeyData}
          refetchChats={getChatData}
          isDisabled={patient.rtmStatus === "Pending"}
        />
      </div>
      {showModal()}
    </div>
  );
};

export default AccordionRow;
